import React from "react";
import PropTypes from "prop-types";
import { isEqual } from "lodash";
import { getFormValues, getFormInitialValues } from "redux-form";
import { useDispatch, useSelector } from "react-redux";

import { Button, Modal } from "rhombus-react";

import { EDIT_VERSION_BLADE_FORM } from "forms/list";

import AdjustFramingPane from "./AdjustFramingPane";
import InlineVersionPreview from "../InlineVersionPreview";
import { updateCropMetadata } from "reducers/Refinements";
import { serializeVariables } from "utils/VersionHelpers";
import { isFrameAdjusted } from "utils/Refinements";

import "./AdjustFramingBlade.css";

export default function AdjustFramingBlade(props) {
	const {
			commonModalProps,
			navigationActions,
			versionInfo,
			saveVersion,
			saveAsVersion,
			libraryId,
			isOnTopOfPreviewBlade = false,
			applyUpdates,
			hidePreviewButton = false,
			shouldUseCredentials = true, /*
				Whether or not we should use credentials for the image crossorigin.
				Generally, when sourced from JCRM, it should be true to avoid 401 errors.
			*/
		} = props
		, dispatch = useDispatch()
		, values = useSelector(getFormValues(EDIT_VERSION_BLADE_FORM)) || {}
		, initialValues = useSelector(getFormInitialValues(EDIT_VERSION_BLADE_FORM)) || {}
		, creativeDetails = useSelector(state => state.creativeDetails)
		, activeVariableName = useSelector(state => state.refinements.activeVariableName)
		, framesAdJusted = isFrameAdjusted(values, initialValues)
		, {
			name,
		} = versionInfo
		, { popModal, pushModal } = navigationActions;

	const closeModal = () => {
		// we need to reset variable crop data to initial values
		// Just crop data cause this is a blade opened over blade which might
		// already hold some modified values. Final save will happen on hitting save there.
		for (const key in values) {
			if (values[key].type !== "Image") {
				continue;
			}
			// resetting just crop data back from initial values only if it was modified
			if (!isEqual(values[key].cropMetadata?.canvas, initialValues[key].cropMetadata?.canvas)) {
				dispatch(updateCropMetadata(key,
					values, initialValues[key].cropMetadata?.canvas,
					initialValues[key].cropMetadata?.hasManualRefinements));
			}
		}
		popModal();
	};

	const onApplyUpdates = () => {
		// Updates are already applied to the form values
		// We just need to close the blade
		// reset to initial values happens in closeModel function
		// TODO: this flow is currently discussed with Tom
		applyUpdates?.(values);
		popModal();
	};

	const previewVersion = (currentValues) => {
		popModal();

		if (!isOnTopOfPreviewBlade) {
			function Configured() {
				let modalProps = {
					commonModalProps: {
						...commonModalProps,
						type: "blade",
					},
					type: "blade",
					libraryId,
					versionInfo,
					creative: creativeDetails,
					currentVariableInfo: serializeVariables(currentValues, initialValues, libraryId),
					saveVersion,
					saveAsVersion,
					pushModal,
					navigationActions,
				};

				return <InlineVersionPreview {...modalProps} />;
			}

			pushModal(Configured);
		}
	};

	const Content = () => (<AdjustFramingPane
		libraryId={libraryId}
		activeVariableName={activeVariableName}
		variableConfig={values}
		shouldUseCredentials={shouldUseCredentials}
	/>);

	const Footer = () => {
		return (
			<>
				<Button
					theme="default"
					className="cypress-adjust-framing-cancel"
					onClick={closeModal}>Cancel</Button>
				<div className="footer-actions">
					{!hidePreviewButton && <Button className="cypress-preview-smart-framed-version" onClick={() => previewVersion(values)}>Preview</Button>}
					<Button disabled={!framesAdJusted}
						theme="green"
						onClick={onApplyUpdates}
						className="cypress-apply-smart-frame-updates">Apply Updates</Button>
				</div>
			</>
		);
	};

	// This should be full screen non toggleable blade
	let modalProps = {
		...commonModalProps,
		isFullScreen: true,
		type: "blade",
		title: `Adjust Framing - ${name}`,
		className: "adjust-framing-blade",
		renderContent: Content,
		renderFooter: Footer,
		onClose: closeModal,
	};

	return <Modal {...modalProps} />;
}

AdjustFramingBlade.displayName = "AdjustFramingBlade";
AdjustFramingBlade.propTypes = {
	commonModalProps: PropTypes.object.isRequired,
	versionInfo: PropTypes.shape({
		name: PropTypes.string,
		variableConfig: PropTypes.object,
	}).isRequired,
};
